import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { Quote, QuoteState, QuoteActions } from '@/types';
import { fetchQuote as fetchQuoteService } from '@/services/quoteService';

/**
 * Combined Zustand store type including state, actions, and async operations
 */
type QuoteStore = QuoteState & QuoteActions & {
  /** Fetch a quote from the API and update store state */
  fetchQuote: () => Promise<void>;
};

/**
 * Zustand store for managing quote state and operations
 * 
 * Features:
 * - Persistent storage with localStorage
 * - Immer integration for immutable updates
 * - DevTools support for debugging
 * - Error handling with fallback quotes
 * - Loading state management
 */
export const useQuoteStore = create<QuoteStore>()(
  devtools(
    persist(
      immer(set => ({
        // Initial state
        currentQuote: null,
        isLoading: false,
        error: null,
        lastUpdated: null,

        // Actions
        setQuote: (quote: Quote) =>
          set(state => {
            state.currentQuote = quote;
            state.lastUpdated = new Date().toISOString();
            state.error = null;
          }),

        setLoading: (loading: boolean) =>
          set(state => {
            state.isLoading = loading;
          }),

        setError: (error: string | null) =>
          set(state => {
            state.error = error;
            state.isLoading = false;
          }),

        clearError: () =>
          set(state => {
            state.error = null;
          }),

        fetchQuote: async () => {
          set(state => {
            state.isLoading = true;
            state.error = null;
          });
          try {
            const quote = await fetchQuoteService();
            set(state => {
              state.currentQuote = quote;
              state.lastUpdated = new Date().toISOString();
              state.isLoading = false;
              state.error = null;
            });
          } catch (error) {
            let errorMessage = 'Failed to fetch quote. Please try again later.';
            if (error instanceof Error) {
              errorMessage = error.message;
            }
            set(state => {
              state.error = errorMessage;
              state.isLoading = false;
              state.currentQuote = {
                hour: new Date().getHours(), // Assign current hour for consistency
                text: 'I must not fear. Fear is the mind-killer. Fear is the little-death that brings total obliteration. I will face my fear. I will permit it to pass over me and through me. And when it has gone past I will turn the inner eye to see its path. Where the fear has gone there will be nothing. Only I will remain.',
                author: 'Paul Atreides',
                source: 'Dune',
                category: 'Mindset',
                created_at: new Date().toISOString(),
              };
            });
          }
        },
      })),
      {
        name: 'primatif-quote-store',
        partialize: state => ({
          currentQuote: state.currentQuote,
          lastUpdated: state.lastUpdated,
        }),
      }
    ),
    {
      name: 'QuoteStore',
    }
  )
);

// Typed selectors for better performance and to prevent unnecessary re-renders

/** Hook to access the current quote from the store */
export const useCurrentQuote = () => useQuoteStore(state => state.currentQuote);

/** Hook to access the loading state from the store */
export const useQuoteLoading = () => useQuoteStore(state => state.isLoading);

/** Hook to access the error state from the store */
export const useQuoteError = () => useQuoteStore(state => state.error);

/** Hook to access the fetchQuote function from the store */
export const useFetchQuote = () => useQuoteStore(state => state.fetchQuote);
